// src/pages/DualResultEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Editor } from "@tinymce/tinymce-react";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

export default function DualResultEdit() {
  const { testId, id } = useParams(); // /dual-results/edit/:testId/:id
  const nav = useNavigate();

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;
 const [summary, setSummary] = useState(""); 
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testName, setTestName] = useState("");
  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([]);

  // TinyMCE ortak init (verdiğin config birebir)
  const tmInit = {
    promotion: false,
    height: 400,
    plugins: [
      "advlist", "autolink", "link", "image", "lists", "charmap",
      "preview", "anchor", "pagebreak", "searchreplace", "wordcount",
      "visualblocks", "code", "fullscreen", "insertdatetime", "media",
      "table", "emoticons", "help"
    ],
    toolbar:
      "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
      "bullist numlist outdent indent | link image | " +
      "print preview media fullscreen code | forecolor backcolor emoticons | help",
    content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
    image_advtab: true,
    automatic_uploads: false,
    images_upload_url: `${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
    images_upload_handler: null,
  };

  // Yardımcılar
  const addPair = () => setBlocks(prev => [...prev, { main_html: "", side_html: "" }]);
  const removePair = (idx) =>
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  const movePair = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const copy = [...blocks];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    setBlocks(copy);
  };
  const updateMain = (idx, content) =>
    setBlocks(prev => prev.map((b, i) => (i === idx ? { ...b, main_html: content } : b)));
  const updateSide = (idx, content) =>
    setBlocks(prev => prev.map((b, i) => (i === idx ? { ...b, side_html: content } : b)));

  // Veri çek
  useEffect(() => {
    (async () => {
      try {
        if (!testId || !id) return;
        const token = localStorage.getItem("admin_token");

        // Test adı
        const testRes = await axios.get(`/api/tests/${testId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const testData = testRes.data?.data || testRes.data; // iki formata da uyumlu
        setTestName(testData?.name || "");

        // Kayıt
        const rowRes = await axios.get(
          `/api/dual-results/tests/${testId}/dual-results/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const row = rowRes.data;
        setTitle(row?.title || "");
        setSummary(row?.summary || "");
        setBlocks(Array.isArray(row?.blocks) ? row.blocks : []);
      } catch (err) {
        console.error("❌ DualResultEdit fetch error:", err);
        toast.error("Kayıt alınamadı");
      } finally {
        setLoading(false);
      }
    })();
  }, [testId, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Başlık zorunlu");
    if (!Array.isArray(blocks) || blocks.length === 0)
      return toast.error("En az bir ikili editör olmalı");

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `/api/dual-results/tests/${testId}/dual-results/${id}`,
        { title: title.trim(), summary, blocks },
        { headers: { Authorization: `Bearer ${token}` } }
      );

            toast.success("Kayıt güncellendi!");
            setTimeout(() => {
              nav(`/test/dual-results/${testId}`);
            }, 1000);
    } catch (err) {
      console.error("❌ DualResultEdit save error:", err);
      toast.error("Güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-4">
        <p>{t("LOADING", "Yükleniyor…")}</p>
      </div>
    );
  }

  return (
    <div className="container my-4">
        <ToastContainer />
<div className="d-flex justify-content-between align-items-center mb-3">
  <div className="small text-muted">
    Test: <strong>{testName || t("DUAL_RESULT_EDIT", "İkili Sonuç Düzenle")}</strong>
  </div>
  <Link to={`/test/dual-results/${testId}`} className="btn btn-outline-secondary btn-sm">
    {t("BACK_TO_LIST", "Listeye Dön")}
  </Link>
</div>


      <form onSubmit={handleSubmit} className="card p-3">
        {/* Başlık */}
        <div className="mb-3">
          <label className="form-label fw-bold">{t("TITLE", "Başlık")}</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            disabled={saving}
            placeholder={t("TITLE_PLACE", "Başlık girin")}
            required
          />
        </div>

 {/* Özet Bilgi */}
        <div className="mb-3">
          <label className="form-label fw-bold">Özet Bilgi</label>
          <Editor
            tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
            value={summary}
            onEditorChange={(content) => setSummary(content)}
            init={{ ...tmInit, height: 300 }}
          />
        </div>
        {/* İkili Editör Setleri */}
        {blocks.map((blk, idx) => (
          <div key={idx} className="mb-4 border rounded p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">{t("EDITOR_PAIR", "Editör Seti")} #{idx + 1}</h6>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => movePair(idx, idx - 1)}
                  disabled={saving || idx === 0}
                  title={t("MOVE_UP", "Yukarı Taşı")}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => movePair(idx, idx + 1)}
                  disabled={saving || idx === blocks.length - 1}
                  title={t("MOVE_DOWN", "Aşağı Taşı")}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removePair(idx)}
                  disabled={saving || blocks.length === 1}
                  title={t("REMOVE_PAIR", "Seti Kaldır")}
                >
                  {t("REMOVE", "Kaldır")}
                </button>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <label className="form-label fw-bold">{t("MAIN_EDITOR", "Ana Açıklama")}</label>
                <Editor
                  tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
                  value={blk.main_html || ""}
                  onEditorChange={(content) => updateMain(idx, content)}
                  init={tmInit}
                />
              </div>

              <div className="col-12 col-lg-6">
                <label className="form-label fw-bold">{t("SIDE_EDITOR", "Yan Açıklama")}</label>
                <Editor
                  tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
                  value={blk.side_html || ""}
                  onEditorChange={(content) => updateSide(idx, content)}
                  init={tmInit}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={addPair} disabled={saving}>
            {t("ADD_EDITOR_PAIR", "İkili Editör Ekle")}
          </button>
          <button type="submit" className="btn btn-primary ms-auto" disabled={saving}>
            {saving ? t("SAVING", "Kaydediliyor…") : t("SAVE", "Kaydet")}
          </button>
        </div>
      </form>
    </div>
  );
}
